import { useState } from 'react';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import { CustomerReportModel, ReportType, UpdateCustomerReportRequest } from '../../../../../Generated/Commodity-Management-Api';
import { useApplicationDispatch } from '../../../../../Redux/ReduxStore';
import { updateCustomerReport } from '../../../../../Redux/Slices/CommodityManagementSlice';
import { GroupingOption } from '../../../../../Types';
import GenericReportSettingsPage from './GenericReportSettingsPage';
import PositionReportSettingsPage from './PositionReportSettingsPage';
import ScenarioReportSettingsPage from './ScenarioReportSettingsPage';
import SummaryReportSettingsPage from './SummaryReportSettingsPage';
import TimeTriggerReportSettingsPage from './TimeTriggerReportSettingsPage';
import VarianceToBudgetReportSettingsPage from './VarianceToBudgetReportSettingsPage';
import ExposureReportSettingsPage from './ExposureReportSettingsPage';
import DailyPositionsReportSettingsPage from './DailyPositionsReportSettingsPage';
import FuturesAllocationReportSettingsPage from './FuturesAllocationReportSettingsPage';
import { useSnackbar } from 'notistack';
import ReportEmailScheduleSection, { reportTypeCanBeEmailed } from './ReportEmailScheduleSection';

interface ReportSettingsEditorProps {
  report: CustomerReportModel;
  groupingOptions: GroupingOption[];
  onDeleteReport: (customerReportId: number) => void;
}

function toUpdateCustomerReportRequest(report: CustomerReportModel): UpdateCustomerReportRequest {
  return {
    customerReportId: report.customerReportId,
    reportType: report.reportType,
    reportName: report.reportName,
    configuration: report.configuration,
  };
}

export default function ReportSettingsEditor({ report, groupingOptions, onDeleteReport }: ReportSettingsEditorProps) {
  const dispatch = useApplicationDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  async function onUpdateReport(updatedReport: CustomerReportModel) {
    setIsSaving(true);

    CommodityManagementApi.updateCustomerReport(null, toUpdateCustomerReportRequest(updatedReport))
      .then(res => {
        const reportToPersist = res.data.customerReport ?? updatedReport;

        enqueueSnackbar(res.data.customerReport?.reportName + ' report updated successfully', { variant: 'success' });

        dispatch(updateCustomerReport({
          customerReportId: reportToPersist.customerReportId,
          reportName: reportToPersist.reportName,
          configuration: reportToPersist.configuration,
        }));
      })

    try {
      const response = await CommodityManagementApi.updateCustomerReport(null, toUpdateCustomerReportRequest(updatedReport));
      const reportToPersist = response.data.customerReport ?? updatedReport;

    } finally {
      setIsSaving(false);
    }
  }

  function renderSettingsPage() {
    switch (report.reportType) {
      case ReportType.MarkToMarket:
        return <PositionReportSettingsPage report={report} groupingOptions={groupingOptions} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.VarianceToBudget:
        return <VarianceToBudgetReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.Scenario:
        return <ScenarioReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.TimeTrigger:
        return <TimeTriggerReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.ProfitAndLoss:
        return <PositionReportSettingsPage report={report} groupingOptions={groupingOptions} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.Summary:
        return <SummaryReportSettingsPage report={report} groupingOptions={groupingOptions} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.Position:
        // The Position report's own columns are not customer-editable; this tab only edits the
        // dashboard's Futures Allocation settings stored inside its configuration.
        return <FuturesAllocationReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.Exposure:
        return <ExposureReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.DailyPositions:
        return <DailyPositionsReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} />;
      case ReportType.MarkToMarketClosed:
        return <GenericReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} description="MTM Closed Positions report shows closed/expired positions with mark-to-market values." />;
      case ReportType.GrossHedging:
        return <GenericReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} description="Gross Hedging report displays hedging positions and their values." />;
      case ReportType.WeightedCostAverage:
        return <GenericReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} description="Weighted Cost Average report calculates weighted average costs across positions." />;
      case ReportType.Combined:
        return <GenericReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} description="Combined report aggregates multiple report types into a single view." />;
      case ReportType.OpenContracts:
        return <GenericReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} description="Open Contracts report shows all currently open contract positions." />;
      case ReportType.PiedmontHedging:
        return <GenericReportSettingsPage report={report} onDeleteReport={onDeleteReport} onUpdateReport={onUpdateReport} isSaving={isSaving} description="Batch workbook combining the Hedging MTM and Hedging Position reports for every location with the Open Contracts summary. Configuration holds the customer name, contract size, and per-location sheet codes." />;
      default:
        return <div>Unsupported report type</div>;
    }
  }

  return (
    <>
      {renderSettingsPage()}
      {/* The Position report appears here as the Futures Allocation settings tab; its email schedule stays out of that tab. */}
      {reportTypeCanBeEmailed(report.reportType) && report.reportType !== ReportType.Position && <ReportEmailScheduleSection report={report} />}
    </>
  );
}
